import { useState } from "react";
import PublicShell, { Block, SampleNote } from "@/components/PublicShell";
import { PhoneCall, Megaphone, Users, Workflow } from "lucide-react";
import useSeo from "@/hooks/useSeo";

const PILLARS = [
  {
    id: "voice", icon: PhoneCall, title: "AI Voice Agents",
    text: "Agents that greet callers, answer questions and book appointments — in English, Hindi or Telugu. Browser demo today; phone lines connect when a telephony provider is set up.",
    href: "/ai-voice-agents",
  },
  {
    id: "marketing", icon: Megaphone, title: "AI Marketing Studio",
    text: "Strategies, posts, emails and ads drafted for your review. Nothing publishes or sends without your explicit approval.",
    href: "/ai-marketing",
  },
  {
    id: "crm", icon: Users, title: "CRM",
    text: "Every lead, appointment and follow-up in one tenant-private workspace — import, filter, and act.",
    href: "/crm",
  },
  {
    id: "automation", icon: Workflow, title: "Automations & Reports",
    text: "Follow-up dates become tasks automatically, and branded PDF reports summarise your real numbers.",
    href: "/product",
  },
];

export default function ProductPage() {
  useSeo("Product — Savy Kerno", "The Savy Kerno platform: AI voice agents that greet callers, answer questions and book appointments, plus CRM and AI marketing tools in one workspace.");
  const [active, setActive] = useState("voice");
  const pillar = PILLARS.find((p) => p.id === active);
  return (
    <PublicShell
      testid="product-page"
      eyebrow="Product"
      title="One workspace for your front desk"
      sub="Savy Kerno brings AI voice agents, customer relationships, appointments and marketing into a single account — so a small team can answer every enquiry and follow up every lead."
    >
      <Block title="Explore the four pillars" testid="product-pillars">
        <div className="grid gap-3 sm:grid-cols-2" role="tablist" aria-label="Platform pillars">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            const on = active === p.id;
            return (
              <button
                key={p.id}
                role="tab"
                aria-selected={on}
                data-testid={`pillar-${p.id}`}
                onClick={() => setActive(p.id)}
                className={`flex items-start gap-3 rounded-2xl border p-5 text-left transition-colors ${on ? "border-[#111111] bg-neutral-50" : "border-neutral-200 hover:border-neutral-300"}`}
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${on ? "bg-[#111111] text-white" : "bg-neutral-100 text-neutral-500"}`}>
                  <Icon size={16} />
                </span>
                <span>
                  <span className="block text-sm font-bold">{p.title}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-neutral-500">{p.text}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-6 rounded-2xl border border-neutral-200 bg-neutral-50/70 p-6" data-testid="pillar-detail">
          <p className="text-sm font-bold">{pillar.title}<SampleNote /></p>
          <p className="mt-2 text-sm leading-relaxed text-neutral-600">{pillar.text}</p>
          {pillar.href !== "/product" && (
            <a href={pillar.href} className="mt-3 inline-block text-xs font-bold underline underline-offset-4" data-testid="pillar-learn-more">
              Learn more about {pillar.title} →
            </a>
          )}
        </div>
      </Block>

      <Block title="How a day looks" testid="product-day">
        <ol className="space-y-3 text-sm text-neutral-600">
          {[
            "Morning: overnight enquiries sit in your CRM with source and consent recorded.",
            "Midday: a customer asks about Friday availability — the agent offers real slots from your calendar.",
            "Afternoon: the marketing studio has three post drafts waiting for your approval.",
            "Evening: tomorrow's follow-ups are already tasks on your dashboard.",
          ].map((s, i) => (
            <li key={i} className="flex gap-3 rounded-xl border border-neutral-200 px-4 py-3" data-testid={`product-day-${i}`}>
              <span className="font-bold text-neutral-300">{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </Block>
    </PublicShell>
  );
}
